import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { DEFAULT_ASSESSMENT_PROCTORING } from '@really-assess/shared/proctoring'
import type { AssessmentProctoringConfig } from '@really-assess/shared/assessments'
import {
  archiveAdminAssessment,
  createAdminAssessment,
  listAdminAssessments,
  updateAdminAssessment,
  type AdminAssessment,
} from '../../services/assessmentsApi'
import { isValidAssessmentSlug, normalizeAssessmentSlug } from '@really-assess/shared/assessments'
import {
  ProctoringConfigFields,
  validateProctoringConfig,
} from '../proctoring/ProctoringConfigFields'

function cloneProctoringConfig(config: AssessmentProctoringConfig): AssessmentProctoringConfig {
  return {
    ...config,
    scoringWeights: { ...config.scoringWeights },
    violationPenalties: { ...config.violationPenalties },
    timeLimit: { ...config.timeLimit },
  }
}

export default function AssessmentForm() {
  const { slug: editSlug } = useParams<{ slug: string }>()
  const isEdit = Boolean(editSlug && editSlug !== 'new')
  const navigate = useNavigate()

  const [loading, setLoading] = useState(isEdit)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showProctoringConfig, setShowProctoringConfig] = useState(false)
  const [showDebugTools, setShowDebugTools] = useState(false)

  const [slug, setSlug] = useState(editSlug && editSlug !== 'new' ? editSlug : '')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [reallyAvatarUrl, setReallyAvatarUrl] = useState('')
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookSecret, setWebhookSecret] = useState('')
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft')
  const [proctoringConfig, setProctoringConfig] = useState<AssessmentProctoringConfig>(() =>
    cloneProctoringConfig(DEFAULT_ASSESSMENT_PROCTORING)
  )

  useEffect(() => {
    if (!isEdit || !editSlug) return
    listAdminAssessments()
      .then((items) => {
        const item = items.find((a) => a.slug === editSlug)
        if (!item) {
          setError('Assessment not found.')
          return
        }
        applyRecord(item)
      })
      .catch(() => setError('Failed to load assessment.'))
      .finally(() => setLoading(false))
  }, [isEdit, editSlug])

  const applyRecord = (item: AdminAssessment) => {
    setSlug(item.slug)
    setTitle(item.title)
    setSubtitle(item.subtitle ?? '')
    setReallyAvatarUrl(item.reallyAvatarUrl)
    setWebhookUrl(item.webhookUrl)
    setWebhookSecret(item.webhookSecret)
    setStatus(item.status)
    setProctoringConfig(cloneProctoringConfig(item.proctoringConfig))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const normalizedSlug = normalizeAssessmentSlug(slug)
    if (!isValidAssessmentSlug(normalizedSlug)) {
      setError('Slug must be kebab-case (e.g. assessment-1, digital-safety-q1).')
      return
    }
    if (!title.trim()) {
      setError('Title is required.')
      return
    }

    const proctoringError = validateProctoringConfig(proctoringConfig)
    if (proctoringError) {
      setError(proctoringError)
      return
    }

    const payload = {
      slug: normalizedSlug,
      title: title.trim(),
      subtitle: subtitle.trim(),
      reallyAvatarUrl: reallyAvatarUrl.trim(),
      webhookUrl: webhookUrl.trim(),
      webhookSecret,
      status,
      proctoringConfig,
    }

    setSaving(true)
    try {
      if (isEdit && editSlug) {
        await updateAdminAssessment(editSlug, payload)
      } else {
        await createAdminAssessment(payload)
      }
      navigate('/admin')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'save_failed'
      if (message === 'slug_already_exists') {
        setError('That slug is already in use. Choose another.')
      } else {
        setError('Failed to save assessment.')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleArchive = async () => {
    if (!isEdit || !editSlug) return
    if (!confirm('Archive this assessment? The link will stop working for learners.')) return
    await archiveAdminAssessment(editSlug)
    navigate('/admin')
  }

  if (loading) return <p className="text-gray-400">Loading...</p>

  const shareUrl = slug ? `${window.location.origin}/${normalizeAssessmentSlug(slug)}` : ''

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <Link to="/admin" className="text-sm text-gray-400 hover:text-white">← Back to list</Link>
        <h2 className="text-2xl font-bold mt-2">{isEdit ? 'Edit assessment' : 'New assessment'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="glass-card p-6 space-y-5">
        <Field label="Title" description="Shown to learners in the header">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="field-input"
            required
          />
        </Field>

        <Field
          label="Subtitle"
          description="Shown under the title on the learner login page and header (replaces the old fixed “Digital Fluency Assessment” text)"
        >
          <input
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            className="field-input"
            placeholder="Stella, your virtual assessor"
          />
        </Field>

        <Field label="Slug" description="URL path — kebab-case, can be renamed later">
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="field-input font-mono"
            placeholder="assessment-1"
            required
          />
          {shareUrl && (
            <p className="text-xs text-primary-300 mt-1">Learner link: {shareUrl}</p>
          )}
        </Field>

        <Field label="ReallyAvatar URL" description="Iframe source during the assessment step">
          <input
            value={reallyAvatarUrl}
            onChange={(e) => setReallyAvatarUrl(e.target.value)}
            className="field-input"
            placeholder="https://reallyavatar.example.com/..."
          />
        </Field>

        <Field label="Integrity webhook URL" description="ReallyAvatar endpoint to receive final score (server-side delivery)">
          <input
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            className="field-input"
            placeholder="https://reallyavatar.example.com/api/integrations/reallyassess/webhook"
          />
        </Field>

        <Field label="Webhook secret" description="Optional HMAC secret — stored securely, never sent to learners">
          <input
            type="password"
            value={webhookSecret}
            onChange={(e) => setWebhookSecret(e.target.value)}
            className="field-input"
            placeholder="whsec_..."
          />
        </Field>

        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            className="field-input"
          >
            <option value="draft">Draft (link disabled)</option>
            <option value="published">Published (link live)</option>
            <option value="archived">Archived</option>
          </select>
        </Field>

        <div>
          <label className="flex items-start gap-2 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={proctoringConfig.browserLockdown !== false}
              onChange={(e) =>
                setProctoringConfig((current) => ({ ...current, browserLockdown: e.target.checked }))
              }
              className="rounded mt-0.5"
            />
            <span>
              Require fullscreen &amp; disable tab switching
              <span className="block text-xs text-gray-500 mt-0.5">
                When on, learners must stay in fullscreen; tab/window switches and fullscreen exits
                trigger warnings and lower the integrity score. When off, none of these are checked.
              </span>
            </span>
          </label>
        </div>

        <CollapsibleSection
          title="Proctoring configuration"
          description="Thresholds, scoring, time limits — stored per assessment."
          expanded={showProctoringConfig}
          onToggle={() => setShowProctoringConfig((open) => !open)}
        >
          <ProctoringConfigFields
            variant="admin"
            value={proctoringConfig}
            onChange={setProctoringConfig}
          />
        </CollapsibleSection>

        <CollapsibleSection
          title="Proctoring debug"
          description="Live webcam testing and global defaults (not shown to learners)."
          expanded={showDebugTools}
          onToggle={() => setShowDebugTools((open) => !open)}
        >
          <div className="space-y-3 text-sm text-gray-400">
            <p>
              Use the proctoring test page to tune thresholds with a live webcam feed, then copy
              settings here or save them per assessment above.
            </p>
            <Link
              to="/proctoring-test"
              target="_blank"
              className="inline-flex items-center gap-2 text-primary-300 hover:text-primary-200"
            >
              Open Proctoring Test
              <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </CollapsibleSection>

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="bg-primary-500 hover:bg-primary-600 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-medium"
          >
            {saving ? 'Saving...' : 'Save assessment'}
          </button>
          {isEdit && (
            <button
              type="button"
              onClick={handleArchive}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Archive
            </button>
          )}
        </div>
      </form>

      <style>{`
        .field-input {
          width: 100%;
          background: rgb(var(--assess-bg-rgb, 15 23 42));
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 0.5rem;
          padding: 0.5rem 0.75rem;
          color: white;
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  )
}

function CollapsibleSection({
  title,
  description,
  expanded,
  onToggle,
  children,
}: {
  title: string
  description: string
  expanded: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <section className="border-t border-assess-border pt-4">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 text-left group"
      >
        <div>
          <h3 className="text-sm font-medium text-gray-300 group-hover:text-white">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
        <span className="text-xs text-gray-500 shrink-0 mt-1">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>
      {expanded && <div className="mt-4">{children}</div>}
    </section>
  )
}

function Field({
  label,
  description,
  children,
}: {
  label: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-sm text-gray-300 mb-1">{label}</label>
      {description && <p className="text-xs text-gray-500 mb-1">{description}</p>}
      {children}
    </div>
  )
}
